import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Loader2, 
  Send, 
  Bell, 
  User,
  Search,
  CheckCircle2,
  AlertTriangle,
  Users,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentUser } from '@/data/users';

const API_URL = '';

export default function SendRemindersPage() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('📝 Marks Entry Reminder');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [priority, setPriority] = useState('high');
  const [term, setTerm] = useState('1');
  
  const currentUser = getCurrentUser();

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      // Get all teachers in the school
      const response = await fetch(
        `${API_URL}/api/auth/users?school_id=${currentUser?.school_id}&role=teacher`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setTeachers(data.filter(u => u.status === 'active'));
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  // Generate default message when term changes
  useEffect(() => {
    setNotificationMessage(
      `Please enter marks for Term ${term} in the Grade Book. ` +
      `The deadline is approaching. If you have already completed this, please disregard this message.`
    );
  }, [term]);

  const filteredTeachers = teachers.filter(teacher => 
    teacher.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectedTeachers.length === filteredTeachers.length) {
      setSelectedTeachers([]);
    } else {
      setSelectedTeachers(filteredTeachers.map(t => t.id));
    }
  };

  const handleToggleTeacher = (teacherId) => {
    setSelectedTeachers(prev => 
      prev.includes(teacherId)
        ? prev.filter(id => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  const handleSendReminders = async () => {
    if (selectedTeachers.length === 0) {
      toast.error('Please select at least one teacher');
      return;
    }

    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      toast.error('Please fill in the notification title and message');
      return;
    }

    setSending(true);
    let successCount = 0;
    let failCount = 0;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      for (const teacherId of selectedTeachers) {
        try {
          const response = await fetch(
            `${API_URL}/api/notifications/send?sender_id=${currentUser.id}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                teacher_id: teacherId,
                type: 'marks_reminder',
                title: notificationTitle,
                message: notificationMessage,
                priority: priority,
                related_term: term,
              }),
            }
          );

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Reminders sent to ${successCount} teacher(s)`);
        setSelectedTeachers([]);
      }
      if (failCount > 0) {
        toast.error(`Failed to send to ${failCount} teacher(s)`);
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="send-reminders-page">
      <div>
        <h1 className="text-2xl font-bold">Send Grade Reminders</h1>
        <p className="text-gray-500 text-sm mt-1">
          Send notifications to teachers to remind them to enter grades
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Teacher Selection */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Select Teachers
                  </CardTitle>
                  <CardDescription>
                    {selectedTeachers.length} of {filteredTeachers.length} selected
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search teachers..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-[200px]"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : filteredTeachers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No teachers found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Select All */}
                  <div 
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    onClick={handleSelectAll}
                  >
                    <Checkbox
                      checked={selectedTeachers.length === filteredTeachers.length && filteredTeachers.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                    <span className="font-medium">Select All ({filteredTeachers.length} teachers)</span>
                  </div>

                  {/* Teacher List */}
                  <div className="max-h-[400px] overflow-y-auto space-y-1">
                    {filteredTeachers.map((teacher) => (
                      <div
                        key={teacher.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedTeachers.includes(teacher.id)
                            ? 'bg-primary/10 border border-primary/20'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                        onClick={() => handleToggleTeacher(teacher.id)}
                        data-testid={`teacher-row-${teacher.id}`}
                      >
                        <Checkbox
                          checked={selectedTeachers.includes(teacher.id)}
                          onCheckedChange={() => handleToggleTeacher(teacher.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium">{teacher.name}</p>
                          <p className="text-sm text-gray-500">
                            {teacher.department || 'No department'}
                          </p>
                        </div>
                        {selectedTeachers.includes(teacher.id) && (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Message Configuration */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Term Selection */}
              <div className="space-y-2">
                <Label>Term</Label>
                <Select value={term} onValueChange={setTerm}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select term" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Term 1</SelectItem>
                    <SelectItem value="2">Term 2</SelectItem>
                    <SelectItem value="3">Term 3</SelectItem>
                    <SelectItem value="4">Term 4</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label>Notification Title</Label>
                <Input
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  placeholder="Enter title..."
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                  placeholder="Enter message..."
                  rows={5}
                />
              </div>

              {/* Send Button */}
              <Button
                className="w-full"
                onClick={handleSendReminders}
                disabled={sending || selectedTeachers.length === 0}
                data-testid="send-reminders-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send to {selectedTeachers.length} Teacher(s)
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-blue-800">Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white p-3 rounded-lg border">
                <p className="font-medium text-sm">{notificationTitle || 'Notification Title'}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {notificationMessage || 'Notification message...'}
                </p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    Term {term}
                  </Badge>
                  <Badge 
                    className={`text-xs ${
                      priority === 'urgent' ? 'bg-red-100 text-red-800' :
                      priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {priority}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
